import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, fullName, formatDate, mensajeError } from '../lib/api';
import { PageHeader, EmptyState, StatusBadge, MiniStat } from '../components/Common';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Pill, Plus, Printer, Trash2, Star, FileText, Upload, Search, Sparkles, AlertCircle, Loader2, Info } from 'lucide-react';
import { toast } from 'sonner';
import { normalizaTexto } from '../lib/utils';
import PdfPreviewDialog from '../components/PdfPreviewDialog';
import { PatientCombobox } from '../components/PatientCombobox';
import { useAuth } from '../lib/auth';
import { isManagement, llevaCatalogoMedicamentos } from '../lib/roles';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// ---------- Subcomponentes ----------
function MedSelector({ medications, value, onChange }) {
  return (
    <Select
      value={value || 'custom'}
      onValueChange={(v) => {
        if (v === 'custom') { onChange(null); return; }
        const m = medications.find(x => x.id === v);
        if (m) onChange(m);
      }}
    >
      <SelectTrigger data-testid="med-selector"><SelectValue placeholder="Elegir medicamento" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="custom">— Escribir medicamento libre —</SelectItem>
        {medications.map(m => (
          <SelectItem key={m.id} value={m.id}>
            {/* La estrella de lucide, como en la tabla de favoritos (5/10/2026). */}
            {m.is_favorite ? <Star className="w-3.5 h-3.5 inline mr-1 text-yellow-500 fill-yellow-400" aria-label="Favorito" /> : null}{m.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function RecipeDialog({ open, onOpenChange, initial, patients, medications, onSaved }) {
  const isEdit = !!initial?.id;
  const empty = { patient_id: '', medication_name: '', presentation: '', dose: '', route: '', posology: '', duration: '', packages: 1, diagnosis: '', patient_instructions: '' };
  const [form, setForm] = useState(empty);

  useEffect(() => {
    if (open) {
      if (initial) setForm({ ...empty, ...initial });
      else setForm(empty);
    }
    // eslint-disable-next-line
  }, [open, initial]);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const applyMedication = (m) => {
    if (!m) { setForm((f) => ({ ...f, medication_id: null })); return; }
    setForm(f => ({
      ...f,
      medication_id: m.id,
      medication_name: m.name,
      presentation: m.presentation || '',
      dose: m.dose || '',
      route: m.route || '',
      posology: m.posology || '',
      duration: m.duration || '',
      packages: m.packages || 1,
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.patient_id) { toast.error('Selecciona un paciente'); return; }
    if (!form.medication_name) { toast.error('Indica el medicamento'); return; }
    try {
      if (isEdit) {
        await api.put(`/api/recetas/${initial.id}`, form);
        toast.success('Receta actualizada');
      } else {
        await api.post('/api/recetas', form);
        toast.success('Receta creada (borrador)');
      }
      onSaved && onSaved();
      onOpenChange(false);
    } catch (err) {
      // Con `mensajeError`: un 422 trae una LISTA en `detail`, y pasada tal
      // cual al aviso se pintaba el objeto en vez del motivo (23/9/2026).
      toast.error(mensajeError(err, 'No se pudo guardar la receta'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isEdit ? 'Editar receta' : 'Nueva receta'}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Paciente *</Label>
            <PatientCombobox
              value={form.patient_id}
              onChange={(v) => setF('patient_id', v)}
              patients={patients}
              disabled={isEdit}
              placeholder="Seleccionar paciente…"
              dataTestid="recipe-patient"
            />
          </div>

          <div>
            <Label>Plantilla (favoritos)</Label>
            <MedSelector medications={medications} value={form.medication_id} onChange={applyMedication} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Medicamento *</Label><Input required value={form.medication_name} onChange={(e) => setF('medication_name', e.target.value)} data-testid="recipe-med-name" /></div>
            <div><Label>Presentación</Label><Input value={form.presentation} onChange={(e) => setF('presentation', e.target.value)} placeholder="Comprimidos 500 mg" /></div>
            <div><Label>Dosis</Label><Input value={form.dose} onChange={(e) => setF('dose', e.target.value)} placeholder="500 mg" /></div>
            <div><Label>Vía</Label><Input value={form.route} onChange={(e) => setF('route', e.target.value)} placeholder="Oral / Oftálmica" /></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2"><Label>Posología *</Label><Input required value={form.posology} onChange={(e) => setF('posology', e.target.value)} placeholder="1 comprimido cada 8 horas" data-testid="recipe-posology" /></div>
            <div><Label>Duración</Label><Input value={form.duration} onChange={(e) => setF('duration', e.target.value)} placeholder="7 días" /></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Nº envases</Label><Input type="number" min={1} value={form.packages} onChange={(e) => setF('packages', parseInt(e.target.value) || 1)} /></div>
            <div><Label>Diagnóstico (opcional)</Label><Input value={form.diagnosis} onChange={(e) => setF('diagnosis', e.target.value)} placeholder="Post-cirugía blefaroplastia" /></div>
          </div>

          <div>
            <Label>Instrucciones al paciente (opcional)</Label>
            <Textarea value={form.patient_instructions} onChange={(e) => setF('patient_instructions', e.target.value)} rows={2} />
          </div>

          <div className="flex items-start gap-1.5 text-xs text-muted-foreground bg-[rgba(46,110,94,0.08)] p-2 rounded">
            <Info className="w-3.5 h-3.5 shrink-0 mt-px" aria-hidden="true" />
            <span>La receta se guarda como <b>borrador</b>. Solo consumirá un número CGCOM al imprimir/exportar.</span>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="recipe-save"><Plus className="w-4 h-4 mr-2" />{isEdit ? 'Guardar' : 'Crear borrador'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function MedicationDialog({ open, onOpenChange, initial, onSaved }) {
  const isEdit = !!initial?.id;
  const empty = { name: '', presentation: '', dose: '', route: '', posology: '', duration: '', packages: 1, is_favorite: true, category: '' };
  const [form, setForm] = useState(empty);
  useEffect(() => { if (open) setForm(initial ? { ...empty, ...initial } : empty); /* eslint-disable-next-line */ }, [open, initial]);
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (isEdit) await api.put(`/api/recetas/medications/${initial.id}`, form);
      else await api.post('/api/recetas/medications', form);
      toast.success(isEdit ? 'Medicamento actualizado' : 'Medicamento añadido a favoritos');
      onSaved && onSaved();
      onOpenChange(false);
    } catch (err) {
      // El motivo como texto (M34, segunda pasada del 23/9/2026): con el
      // `detail` crudo, un 422 pintaba la lista de Pydantic en el aviso. El
      // diálogo sigue abierto con lo escrito.
      toast.error(mensajeError(err, 'No se pudo guardar el medicamento'));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{isEdit ? 'Editar medicamento' : 'Nuevo medicamento favorito'}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Nombre *</Label><Input required value={form.name} onChange={(e) => setF('name', e.target.value)} data-testid="med-name" /></div>
          <div><Label>Presentación</Label><Input value={form.presentation} onChange={(e) => setF('presentation', e.target.value)} placeholder="Comprimidos 500 mg" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Dosis</Label><Input value={form.dose} onChange={(e) => setF('dose', e.target.value)} /></div>
            <div><Label>Vía</Label><Input value={form.route} onChange={(e) => setF('route', e.target.value)} placeholder="Oral / Oftálmica" /></div>
          </div>
          <div><Label>Posología</Label><Input value={form.posology} onChange={(e) => setF('posology', e.target.value)} placeholder="1 comp cada 8 horas" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Duración</Label><Input value={form.duration} onChange={(e) => setF('duration', e.target.value)} placeholder="7 días" /></div>
            <div><Label>Nº envases</Label><Input type="number" min={1} value={form.packages} onChange={(e) => setF('packages', parseInt(e.target.value) || 1)} /></div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="med-save"><Plus className="w-4 h-4 mr-2" />{isEdit ? 'Guardar' : 'Añadir'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Página principal ----------
export default function RecipesPage() {
  const { user } = useAuth();
  const mgmt = isManagement(user);
  // Añadir, editar y borrar favoritos: la misma lista que el servidor (M34).
  const catalogo = llevaCatalogoMedicamentos(user);
  const [recipes, setRecipes] = useState([]);
  const [patients, setPatients] = useState([]);
  const [medications, setMedications] = useState([]);
  const [pool, setPool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorCarga, setErrorCarga] = useState('');
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  // Dialogs
  const [recipeOpen, setRecipeOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [medOpen, setMedOpen] = useState(false);
  const [editingMed, setEditingMed] = useState(null);
  const [preview, setPreview] = useState({ open: false, url: '', filename: '', title: '' });
  // Selección múltiple (2026-08-27): imprimir varias recetas en un solo PDF.
  const [seleccion, setSeleccion] = useState(() => new Set());
  const alternarSel = (id) => setSeleccion((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const [uploadBusy, setUploadBusy] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2, r3, r4] = await Promise.all([
        api.get('/api/recetas'),
        api.get('/api/patients'),
        api.get('/api/recetas/medications'),
        api.get('/api/recetas/pool/status'),
      ]);
      setRecipes(r1.data);
      setPatients(r2.data);
      setMedications(r3.data);
      setPool(r4.data);
      setErrorCarga('');
    } catch (err) {
      // Un error no se enseña como dato (23/9/2026): sin esto la promesa se
      // perdía y la pantalla decía «Sin recetas» y «Sin favoritos», como si
      // no hubiera ninguna.
      setErrorCarga(mensajeError(err, 'No se pudieron cargar las recetas'));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const visible = useMemo(() => {
    let arr = recipes;
    if (statusFilter !== 'all') arr = arr.filter(r => r.status === statusFilter);
    if (q) {
      const qq = normalizaTexto(q);
      arr = arr.filter(r => normalizaTexto(r.patient_name).includes(qq) || normalizaTexto(r.medication_name).includes(qq));
    }
    return arr;
  }, [recipes, q, statusFilter]);

  const onUploadXml = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/api/recetas/pool/import-xml', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Importados ${r.data.inserted} números${r.data.duplicates ? ` · ${r.data.duplicates} duplicados omitidos` : ''}`);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al importar XML');
    } finally {
      setUploadBusy(false);
      e.target.value = '';
    }
  };

  // Los dos borrados capturan el error y lo dicen (M34, segunda pasada del
  // 23/9/2026): antes el `await` iba suelto, la promesa rechazada se perdía y
  // quien pulsaba no sabía si se había borrado o no.
  const onDelete = async (r) => {
    if (!window.confirm(`¿Eliminar receta de ${r.patient_name}?`)) return;
    try {
      await api.delete(`/api/recetas/${r.id}`);
    } catch (err) {
      toast.error(mensajeError(err, 'No se pudo eliminar la receta'));
      return;
    }
    toast.success('Eliminada');
    fetchAll();
  };

  const onDeleteMed = async (m) => {
    if (!window.confirm(`¿Eliminar "${m.name}" del catálogo?`)) return;
    try {
      await api.delete(`/api/recetas/medications/${m.id}`);
    } catch (err) {
      toast.error(mensajeError(err, 'No se pudo eliminar el medicamento'));
      return;
    }
    toast.success('Medicamento eliminado');
    fetchAll();
  };

  const openPdf = (r) => {
    setPreview({
      open: true,
      url: `${BACKEND_URL}/api/recetas/${r.id}/pdf`,
      filename: `receta_${r.numero || 'borrador'}.pdf`,
      title: `Receta · ${r.medication_name} · ${r.patient_name}`,
    });
  };

  const imprimirLote = () => {
    const ids = Array.from(seleccion);
    if (!ids.length) return;
    setPreview({
      open: true,
      url: `${BACKEND_URL}/api/recetas/lote/pdf?ids=${ids.join(',')}`,
      filename: `recetas_lote_${ids.length}.pdf`,
      title: `${ids.length} recetas seleccionadas`,
    });
    setSeleccion(new Set());
  };

  return (
    <div>
      <PageHeader
        title="Recetas"
        subtitle="Emisión de recetas privadas CGCOM. El número CGCOM solo se consume al imprimir."
        actions={
          <>
            {/* Phase 27.10 — Contador de recetas restantes junto al botón de
                subir: en verde con stock, ámbar ≤20, rojo ≤5 (toca subir XML). */}
            {pool && (pool.total ?? 0) > 0 && (
              <span
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium tabular-nums ${
                  pool.reciclando
                    ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                    : (pool.available ?? 0) <= 5
                      ? 'border-red-300 bg-red-50 text-red-800'
                      : (pool.available ?? 0) <= 20
                        ? 'border-amber-300 bg-amber-50 text-amber-800'
                        : 'border-emerald-300 bg-emerald-50 text-emerald-800'
                }`}
                title={pool.reciclando
                  ? `Lote agotado: la numeración ha vuelto al principio (vuelta ${pool.vuelta}). Siguiente: ${pool.siguiente?.numero || '—'}`
                  : `Números CGCOM disponibles: ${pool.available ?? 0} de ${pool.total ?? 0}`}
                data-testid="rx-pool-counter"
              >
                <Pill className="w-3.5 h-3.5" />
                {pool.reciclando
                  ? <>Vuelta {pool.vuelta} · nº {pool.siguiente?.numero || '—'}</>
                  : <>Quedan {pool.available ?? 0} recetas</>}
              </span>
            )}
            {mgmt && (
              <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="rx-header-upload-label">
                <input
                  type="file"
                  accept=".xml,application/xml,text/xml"
                  className="hidden"
                  onChange={onUploadXml}
                  disabled={uploadBusy}
                  data-testid="rx-header-upload-input"
                />
                <Button asChild variant="outline" className="border-[#2E6E5E] text-[#13231E] hover:bg-[rgba(46,110,94,0.06)]" disabled={uploadBusy} data-testid="rx-header-upload-btn">
                  <span>
                    {uploadBusy
                      ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Subiendo…</>)
                      : (<><Upload className="w-4 h-4 mr-2" /> Subir recetas</>)}
                  </span>
                </Button>
              </label>
            )}
            <Button onClick={() => { setEditing(null); setRecipeOpen(true); }} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="rx-new"><Plus className="w-4 h-4 mr-2" /> Nueva receta</Button>
          </>
        }
      />

      {/* Si no carga, se dice y se puede reintentar (23/9/2026). Los números y
          las pestañas no se pintan: con las listas vacías dirían «0 recetas»
          y «Sin favoritos», que es un error enseñado como dato. */}
      {errorCarga && (
        <div className="cjo-card p-4 mb-4 border-l-4 border-l-red-500 bg-red-50 flex items-center gap-3" role="alert" data-testid="rx-error-carga">
          <AlertCircle className="w-5 h-5 text-red-700 shrink-0" />
          <div className="text-sm text-red-900 flex-1">
            <b>No se han podido cargar las recetas.</b> {errorCarga}
          </div>
          <Button size="sm" variant="outline" onClick={fetchAll} disabled={loading} data-testid="rx-reintentar">Reintentar</Button>
        </div>
      )}

      {!errorCarga && (<>
      {/* KPIs Pool */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="rx-kpis">
        <MiniStat
          label={pool?.reciclando ? 'Sin estrenar' : 'Números disponibles'}
          value={pool?.available ?? '—'}
          hint={pool?.reciclando
            ? `los ${pool?.total ?? 0} del lote ya se han usado al menos una vez`
            : `de ${pool?.total ?? 0} en el lote`}
          icon={Pill}
        />
        <MiniStat
          label="Próximo número"
          value={pool?.siguiente?.numero || '—'}
          hint={pool?.reciclando
            ? `Vuelta ${pool?.vuelta} — la numeración empieza otra vez`
            : (pool?.siguiente?.cve || (pool?.total > 0 ? 'Reciclará un número anterior' : 'Importa un XML CGCOM'))}
          icon={Sparkles}
        />
        <MiniStat label="Consumidos" value={pool?.consumed ?? '—'} hint="recetas impresas" icon={FileText} />
        <MiniStat label="Total recetas" value={recipes.length} hint={`${recipes.filter(r => r.status === 'draft').length} borradores · ${recipes.filter(r => r.status === 'printed').length} impresas`} icon={FileText} />
      </div>

      {/* Aviso solo cuando NO se ha importado ningún lote todavía. */}
      {pool && (pool.total ?? 0) === 0 && (
        <div className="cjo-card p-3 mb-4 border-l-4 border-l-[#2E6E5E] bg-[rgba(46,110,94,0.08)] flex items-center gap-3" data-testid="rx-pool-empty">
          <AlertCircle className="w-5 h-5 text-[#13231E]" />
          <div className="text-sm text-[#13231E] flex-1">
            <b>Sin lotes importados.</b> Sube un XML CGCOM para empezar a emitir recetas con número oficial.
          </div>
        </div>
      )}

      {/* Phase 27.10 — Aviso de stock bajo: quedan pocos números y conviene
          subir otro XML CGCOM antes de que el sistema recicle números ya
          consumidos (reutilizar CVEs no es deseable). */}
      {pool && (pool.total ?? 0) > 0 && (pool.available ?? 0) <= 20 && (
        <div
          className={`cjo-card p-3 mb-4 border-l-4 flex items-center gap-3 ${
            pool.reciclando
              ? 'border-l-[#2E6E5E] bg-[rgba(46,110,94,0.08)]'
              : (pool.available ?? 0) <= 5
                ? 'border-l-red-500 bg-red-50'
                : 'border-l-amber-500 bg-amber-50'
          }`}
          data-testid="rx-pool-low"
        >
          <AlertCircle className={`w-5 h-5 ${pool.reciclando ? 'text-[#13231E]' : (pool.available ?? 0) <= 5 ? 'text-red-700' : 'text-amber-700'}`} />
          <div className={`text-sm flex-1 ${pool.reciclando ? 'text-[#13231E]' : (pool.available ?? 0) <= 5 ? 'text-red-900' : 'text-amber-900'}`}>
            {pool.reciclando
              ? (<><b>El lote se ha agotado y la numeración ha vuelto a empezar.</b> Se sigue emitiendo por el nº {pool.siguiente?.numero || '—'} (vuelta {pool.vuelta}). Si prefieres números nuevos, sube otro XML del Colegio.</>)
              : (<><b>Quedan {pool.available} recetas disponibles.</b> Sube otro XML CGCOM pronto para no quedarte sin números.</>)}
          </div>
        </div>
      )}

      <Tabs defaultValue="recipes">
        <TabsList>
          <TabsTrigger value="recipes" data-testid="rx-tab-recipes">Recetas emitidas</TabsTrigger>
          <TabsTrigger value="meds" data-testid="rx-tab-meds">Medicamentos favoritos</TabsTrigger>
          <TabsTrigger value="pool" data-testid="rx-tab-pool">Lotes CGCOM</TabsTrigger>
        </TabsList>

        <TabsContent value="recipes" className="mt-4">
          <div className="cjo-card p-4 mb-3 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9" placeholder="Buscar paciente o medicamento…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="rx-search" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[200px]" data-testid="rx-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="draft">Borradores</SelectItem>
                <SelectItem value="printed">Impresas</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {seleccion.size > 0 && (
            <div className="mb-2 flex items-center gap-2">
              <Button
                size="sm"
                onClick={imprimirLote}
                className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                title="Las que estén en borrador se emiten (consumen su Nº CGCOM) y salen todas en un único PDF"
                data-testid="rx-imprimir-lote"
              >
                <Printer className="w-4 h-4 mr-1" /> Imprimir seleccionadas ({seleccion.size})
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSeleccion(new Set())}>Quitar selección</Button>
            </div>
          )}
          <div className="cjo-card overflow-hidden">
            {loading ? <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>
              : visible.length === 0
              ? <EmptyState icon={Pill} title="Sin recetas" description="Crea tu primera receta o impórtalas desde el panel de quirófano." />
              : <div className="overflow-x-auto"><table className="w-full text-sm">
                <thead className="bg-[rgba(46,110,94,0.04)] text-left">
                  <tr>
                    <th className="py-3 pl-4 pr-1 w-8"></th>
                    <th className="py-3 px-4 font-semibold text-[#13231E]">Nº</th>
                    <th className="py-3 px-4 font-semibold text-[#13231E]">Paciente</th>
                    <th className="py-3 px-4 font-semibold text-[#13231E]">Medicamento</th>
                    <th className="py-3 px-4 font-semibold text-[#13231E]">Posología</th>
                    <th className="py-3 px-4 font-semibold text-[#13231E]">Fecha</th>
                    <th className="py-3 px-4 font-semibold text-[#13231E]">Estado</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map(r => (
                    <tr key={r.id} className="border-t border-border hover:bg-[rgba(46,110,94,0.03)]" data-testid="rx-row">
                      <td className="py-3 pl-4 pr-1">
                        <input
                          type="checkbox"
                          className="w-4 h-4 accent-[#2E6E5E] cursor-pointer"
                          checked={seleccion.has(r.id)}
                          onChange={() => alternarSel(r.id)}
                          title="Marcar para imprimir en lote"
                          data-testid={`rx-check-${r.id}`}
                        />
                      </td>
                      <td className="py-3 px-4 font-mono text-xs">{r.numero || '—'}</td>
                      <td className="py-3 px-4 font-medium">{r.patient_name}</td>
                      <td className="py-3 px-4">{r.medication_name}{r.presentation ? <div className="text-xs text-muted-foreground">{r.presentation}</div> : null}</td>
                      <td className="py-3 px-4 text-xs">{r.posology}{r.duration ? ` · ${r.duration}` : ''}</td>
                      <td className="py-3 px-4 text-xs">{formatDate(r.printed_at || r.created_at)}</td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full border text-xs font-medium ${r.status === 'printed' ? 'bg-[rgba(46,110,94,0.10)] text-[#13231E] border-[rgba(46,110,94,0.22)]' : 'bg-[rgba(46,110,94,0.12)] text-[#13231E] border-[rgba(46,110,94,0.28)]'}`}>
                          {r.status === 'printed' ? 'Impresa' : 'Borrador'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex gap-1 items-center justify-end flex-wrap">
                          <Button size="sm" variant="outline" onClick={() => openPdf(r)} title={r.status === 'draft' ? 'Generar e imprimir (consume Nº CGCOM)' : 'Ver receta'} data-testid="rx-pdf-button">
                            <FileText className="w-4 h-4 mr-1" /> {r.status === 'draft' ? 'Imprimir' : 'Ver'}
                          </Button>
                          {r.status === 'draft' && (
                            <Button size="sm" variant="ghost" onClick={() => { setEditing(r); setRecipeOpen(true); }}>Editar</Button>
                          )}
                          {/* Una receta EMITIDA (impresa, o con su número
                              oficial del Colegio) no se borra: el servidor
                              contesta 409 (23/9/2026). No se ofrece la
                              papelera; si se emite mientras tanto, onDelete
                              enseña el motivo del servidor. */}
                          {r.status !== 'printed' && !r.cve && (
                            <Button size="sm" variant="ghost" onClick={() => onDelete(r)} className="text-destructive" title="Eliminar receta" data-testid="rx-delete"><Trash2 className="w-3.5 h-3.5" /></Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            }
          </div>
        </TabsContent>

        <TabsContent value="meds" className="mt-4">
          {/* Añadir, editar y borrar, solo a quien el servidor se lo deja
              (M34, segunda pasada del 23/9/2026). Recepción ve la lista:
              la necesita para recetar. */}
          {catalogo ? (
            <div className="flex justify-end mb-3">
              <Button onClick={() => { setEditingMed(null); setMedOpen(true); }} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="med-new"><Plus className="w-4 h-4 mr-2" /> Añadir favorito</Button>
            </div>
          ) : (
            <div className="text-xs text-muted-foreground italic mb-3" data-testid="med-solo-lectura">Las plantillas las llevan los sanitarios y dirección.</div>
          )}
          <div className="cjo-card overflow-hidden">
            {medications.length === 0
              ? <EmptyState icon={Star} title="Sin favoritos" description={catalogo ? 'Añade plantillas de medicamentos frecuentes.' : 'Todavía no hay plantillas de medicamentos.'} />
              : <div className="overflow-x-auto"><table className="w-full text-sm">
                <thead className="bg-[rgba(46,110,94,0.04)] text-left">
                  <tr>
                    <th className="py-3 px-4 font-semibold text-[#13231E]">Medicamento</th>
                    <th className="py-3 px-4 font-semibold text-[#13231E]">Presentación</th>
                    <th className="py-3 px-4 font-semibold text-[#13231E]">Posología</th>
                    <th className="py-3 px-4 font-semibold text-[#13231E]">Duración</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {medications.map(m => (
                    <tr key={m.id} className="border-t border-border hover:bg-[rgba(46,110,94,0.03)]" data-testid="med-row">
                      <td className="py-3 px-4 font-medium">{m.is_favorite ? <Star className="w-3.5 h-3.5 inline mr-1 text-yellow-500 fill-yellow-400" /> : null}{m.name}</td>
                      <td className="py-3 px-4 text-xs">{m.presentation || '—'}</td>
                      <td className="py-3 px-4 text-xs">{m.posology}</td>
                      <td className="py-3 px-4 text-xs">{m.duration}</td>
                      <td className="py-3 px-4 text-right">
                        {catalogo && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => { setEditingMed(m); setMedOpen(true); }} data-testid="med-edit">Editar</Button>
                            <Button size="sm" variant="ghost" onClick={() => onDeleteMed(m)} className="text-destructive" title="Eliminar del catálogo" data-testid="med-delete"><Trash2 className="w-3.5 h-3.5" /></Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            }
          </div>
        </TabsContent>

        <TabsContent value="pool" className="mt-4">
          <div className="cjo-card p-5 mb-4">
            <h3 className="font-display font-semibold text-[#13231E] mb-2">Importar lote CGCOM</h3>
            <p className="text-sm text-muted-foreground mb-3">Sube un archivo XML del Colegio de Médicos con los números de receta (CVE) prepagados. Los números se añaden al pool y se consumen al imprimir.</p>
            {mgmt ? (
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="file" accept=".xml,application/xml,text/xml" className="hidden" onChange={onUploadXml} disabled={uploadBusy} data-testid="rx-pool-upload" />
                <Button asChild className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" disabled={uploadBusy}>
                  <span>{uploadBusy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Subiendo…</> : <><Upload className="w-4 h-4 mr-2" /> Subir XML CGCOM</>}</span>
                </Button>
              </label>
            ) : <div className="text-xs text-muted-foreground italic">Solo dirección puede importar lotes.</div>}
          </div>

          <div className="cjo-card overflow-hidden">
            {(!pool?.lots || pool.lots.length === 0)
              ? <EmptyState icon={Upload} title="Sin lotes importados" description="Sube tu primer XML CGCOM." />
              : <div className="overflow-x-auto"><table className="w-full text-sm">
                <thead className="bg-[rgba(46,110,94,0.04)] text-left">
                  <tr>
                    <th className="py-3 px-4 font-semibold text-[#13231E]">Lote</th>
                    <th className="py-3 px-4 font-semibold text-[#13231E]">Colegiado</th>
                    <th className="py-3 px-4 font-semibold text-[#13231E]">Rango</th>
                    <th className="py-3 px-4 font-semibold text-[#13231E]">Importado</th>
                    <th className="py-3 px-4 font-semibold text-[#13231E]">Insertados</th>
                  </tr>
                </thead>
                <tbody>
                  {pool.lots.map(l => (
                    <tr key={l.id} className="border-t border-border" data-testid="rx-pool-lot-row">
                      <td className="py-3 px-4 font-mono text-xs">#{l.numero_lote || '—'}</td>
                      <td className="py-3 px-4 text-xs">{l.colegiado?.nombre} {l.colegiado?.apellido1} {l.colegiado?.apellido2} · Col. {l.colegiado?.numero_colegiado}</td>
                      <td className="py-3 px-4 font-mono text-xs">{l.range_from} → {l.range_to}</td>
                      <td className="py-3 px-4 text-xs">{formatDate(l.imported_at)}</td>
                      <td className="py-3 px-4 text-xs">{l.inserted}/{l.total_numbers}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            }
          </div>
        </TabsContent>
      </Tabs>
      </>)}

      <RecipeDialog open={recipeOpen} onOpenChange={setRecipeOpen} initial={editing} patients={patients} medications={medications} onSaved={fetchAll} />
      <MedicationDialog open={medOpen} onOpenChange={setMedOpen} initial={editingMed} onSaved={fetchAll} />
      <PdfPreviewDialog open={preview.open} onOpenChange={(o) => { setPreview({ ...preview, open: o }); if (!o) fetchAll(); }} url={preview.url} filename={preview.filename} title={preview.title} />
    </div>
  );
}
